import { normalizePhone } from './phoneHelpers'

const MAX_EDIT_DISTANCE = 1

const toSearchString = (value?: string | null) => value ?? ''

export const normalizeSearchText = (text?: string | null) =>
    toSearchString(text)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()

export const levenshteinDistance = (a: string, b: string): number => {
    if (a === b) return 0
    if (!a.length) return b.length
    if (!b.length) return a.length

    const prev = Array.from({ length: b.length + 1 }, (_, i) => i)
    const curr = new Array(b.length + 1)

    for (let i = 1; i <= a.length; i++) {
        curr[0] = i
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1
            curr[j] = Math.min(
                curr[j - 1] + 1,
                prev[j] + 1,
                prev[j - 1] + cost
            )
        }
        for (let j = 0; j <= b.length; j++) {
            prev[j] = curr[j]
        }
    }

    return prev[b.length]
}

export const allowedEditDistance = (queryLength: number) => {
    if (queryLength <= 3) return 0
    return MAX_EDIT_DISTANCE
}

const wordFuzzyMatch = (word: string, query: string) => {
    if (!query) return true
    if (word.includes(query)) return true

    const maxDistance = allowedEditDistance(query.length)
    if (maxDistance === 0) return false

    // Only allow typos when the query is about as long as the word.
    // Avoid matching a longer query like "korotoumou" to shorter "korotoum".
    if (query.length > word.length + maxDistance) return false
    if (word.length > query.length + maxDistance) {
        for (let i = 0; i <= word.length - query.length; i++) {
            const slice = word.slice(i, i + query.length)
            if (levenshteinDistance(slice, query) <= maxDistance) {
                return true
            }
        }
        return false
    }

    return levenshteinDistance(word, query) <= maxDistance
}

export const fuzzyMatchText = (
    haystack?: string | null,
    needle?: string | null
): boolean => {
    const text = normalizeSearchText(haystack)
    const query = normalizeSearchText(needle)

    if (!query) return false
    if (text.includes(query)) return true

    const queryWords = query.split(' ').filter(Boolean)
    const textWords = text.split(' ').filter(Boolean)

    return queryWords.every((queryWord) =>
        textWords.some((textWord) => wordFuzzyMatch(textWord, queryWord))
    )
}

export const fuzzyMatchPhone = (
    haystack?: string | null,
    needle?: string | null
): boolean => {
    const phone = normalizePhone(haystack)
    const query = normalizePhone(needle)

    if (!query || !phone) return false
    if (phone.includes(query) || query.includes(phone)) return true

    const maxDistance = allowedEditDistance(query.length)
    if (maxDistance === 0) return false

    // Digits-only typo tolerance for near-equal length numbers
    if (Math.abs(phone.length - query.length) > maxDistance) return false
    return levenshteinDistance(phone, query) <= maxDistance
}

export const fuzzyMatchCode = (
    haystack?: string | null,
    needle?: string | null
): boolean => {
    const code = normalizeSearchText(haystack)
    const query = normalizeSearchText(needle)
    if (!query) return false
    return code.includes(query)
}
